import { ArrayFieldTemplateProps } from '@rjsf/utils';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { AddButton } from './ButtonTemplates';
import { formatSchemaName } from '@/shared/utils/formatSchemaName';

export const CustomArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  const theme = useTheme();
  // Get the display name for this array type
  const itemsSchema = props.schema.items;
  let itemName = "Items";
  if (itemsSchema && typeof itemsSchema === 'object' && !Array.isArray(itemsSchema) && (itemsSchema as any).title) {
      itemName = formatSchemaName((itemsSchema as any).title);
  } else if (props.title) {
      itemName = formatSchemaName(props.title);
  }

  const singularize = (text: string) => {
    // Exceptions for words that end in 's' but are already singular or shouldn't be changed
    const exceptions = ["As", "News", "Lens", "Analysis", "Status"];
    const words = text.split(' ');
    const lastWord = words[words.length - 1];
    
    if (exceptions.includes(lastWord)) return text;

    if (text.endsWith("ies")) return text.slice(0, -3) + "y";
    if (text.endsWith("sses")) return text.slice(0, -2);
    if (text.endsWith("s") && !text.endsWith("ss")) return text.slice(0, -1);
    return text;
  };

  const buttonLabel = `Add New ${singularize(itemName)}`;

  // Top-level array sections have a $id like "root_Subjects". Tag them so the CO-review
  // sidebar's click-to-scroll can find them via querySelector. RJSF v6 exposes this as
  // `fieldPathId`; v5 used `idSchema` — read both for safety.
  const p = props as {
    fieldPathId?: { $id?: string };
    idSchema?: { $id?: string };
  };
  const id = p.fieldPathId?.$id ?? p.idSchema?.$id ?? "";
  const topLevelMatch = /^root_([A-Za-z0-9]+)$/.exec(id);
  const sectionKey = topLevelMatch ? topLevelMatch[1] : undefined;

  // Section heading. Top-level array sections (Subjects, Vehicles, etc.) need their
  // own title — RJSF v6 doesn't pass the array title through FieldTemplate the way v5
  // did, so we render it here. Use the same prettified label as the sidebar.
  const sectionTitle = sectionKey
    ? formatSchemaName(sectionKey)
    : props.title
      ? formatSchemaName(props.title)
      : undefined;

  return (
    <div data-section-key={sectionKey}>
      {sectionTitle && (
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1.5, mt: sectionKey ? 2 : 0 }}
        >
          {sectionTitle}
        </Typography>
      )}
      {/* Empty state when no items exist */}
      {props.items.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            px: 2,
            border: '2px dashed',
            borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.5) : 'grey.300',
            borderRadius: 2,
            mb: 2,
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : 'grey.50',
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 48, color: theme.palette.mode === 'dark' ? 'text.disabled' : 'grey.400', mb: 1 }} />
          <Typography variant="body1" fontWeight={500} color="text.secondary">
            {props.readonly ? `No ${itemName} recorded` : `No ${itemName} added yet`}
          </Typography>
          {!props.readonly && (
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              Click below to add your first item
            </Typography>
          )}
        </Box>
      ) : (
        // Render existing items
        props.items.map((item) => item)
      )}

      {/* Add button — hidden in readonly mode, since users can't act on it. */}
      {!props.readonly && props.canAdd && !props.disabled && (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <AddButton
            onClick={props.onAddClick}
            disabled={false}
            registry={props.registry}
            label={buttonLabel}
          />
        </div>
      )}
    </div>
  );
};